/**
 * Componente PIPComments
 * 
 * Sistema simplificado de comentários para a janela Picture-in-Picture
 * Permite adicionar comentários rapidamente sem sair da janela PIP
 * 
 * @author VISION DESK
 * @version 0.24.0
 */

import React, { useState } from 'react';
import { MessageSquare, Send, Loader2 } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { osCommentApi } from '@/services/osCommentApi';
import type { PIPCommentsProps } from '@/types/pictureInPicture';

export const PIPComments: React.FC<PIPCommentsProps> = ({
  osId,
  comments,
  onCommentAdded,
}) => {
  const [commentText, setCommentText] = useState('');
  const maxLength = 2000;

  // Mutation para adicionar comentário (INTEGRADO: API real + metadado PIP)
  const addCommentMutation = useMutation({
    mutationFn: async (comment: string) => {
      // console.log('📺 PIP: Enviando comentário via Picture-in-Picture');
      // Adicionar [PIP] no início do comentário para identificar origem
      const commentWithSource = `[PIP] ${comment}`;
      return osCommentApi.createComment(osId, { comment: commentWithSource });
    },
    onSuccess: () => {
      // console.log('✅ PIP: Comentário salvo com sucesso');
      toast.success('💬 Comentário adicionado via PIP');
      setCommentText('');
      onCommentAdded();
    },
    onError: (error: any) => {
      // console.error('❌ PIP: Erro ao adicionar comentário:', error);
      toast.error(error.response?.data?.message || 'Erro ao adicionar comentário');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    const trimmedComment = commentText.trim();
    if (!trimmedComment) {
      toast('Digite um comentário', {
        icon: '⚠️',
        style: {
          background: '#f59e0b',
          color: '#fff'
        }
      });
      return;
    }

    if (trimmedComment.length > maxLength) {
      toast.error(`Comentário muito longo (máximo ${maxLength} caracteres)`);
      return;
    }

    addCommentMutation.mutate(trimmedComment);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Ctrl+Enter ou Cmd+Enter para enviar
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSubmit(e as any);
    }
  };

  const remainingChars = maxLength - commentText.length;
  const isNearLimit = remainingChars < 100;

  return (
    <div className="space-y-3">
      {/* Formulário de Novo Comentário */}
      <form onSubmit={handleSubmit} className="space-y-2">
        <div className="relative">
          <textarea
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Adicionar comentário... (Ctrl+Enter para enviar)"
            disabled={addCommentMutation.isPending}
            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg
                     text-white placeholder-gray-500 resize-none
                     focus:outline-none focus:ring-2 focus:ring-purple-500/50
                     disabled:opacity-50 disabled:cursor-not-allowed
                     text-sm"
            rows={3}
            maxLength={maxLength}
          />
          
          {/* Contador de caracteres */}
          <div
            className={`absolute bottom-2 right-2 text-xs ${
              isNearLimit ? 'text-yellow-400' : 'text-gray-500'
            }`}
          >
            {commentText.length}/{maxLength}
          </div>
        </div>

        {/* Botões */}
        <div className="flex gap-2">
          <button
            type="submit"
            disabled={!commentText.trim() || addCommentMutation.isPending}
            className="flex-1 py-2 px-4 bg-blue-500/20 hover:bg-blue-500/30
                     text-blue-400 rounded-lg transition-colors
                     disabled:opacity-50 disabled:cursor-not-allowed
                     flex items-center justify-center gap-2 text-sm font-medium"
          >
            {addCommentMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Enviando...
              </>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Enviar
              </>
            )}
          </button>
          
          {commentText.trim() && (
            <button
              type="button"
              onClick={() => setCommentText('')}
              disabled={addCommentMutation.isPending}
              className="px-4 py-2 bg-gray-500/20 hover:bg-gray-500/30
                       text-gray-300 rounded-lg transition-colors text-sm"
            >
              Limpar
            </button>
          )}
        </div>
      </form>

      {/* Comentários Recentes */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-sm text-gray-400">
          <MessageSquare className="w-4 h-4" />
          <span>Comentários Recentes:</span>
        </div>

        {comments && comments.length > 0 ? (
          <div className="space-y-2 max-h-40 overflow-y-auto custom-scrollbar">
            {comments.slice(0, 3).map((comment: any, index) => (
              <div
                key={index}
                className="p-2 bg-white/5 rounded border border-white/10 text-sm"
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-blue-400 font-medium text-xs">
                    {comment.user?.name || 'Usuário'}
                  </span>
                  <span className="text-gray-500 text-xs">
                    {new Date(comment.createdAt).toLocaleTimeString('pt-BR', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
                <p className="text-gray-300 text-xs line-clamp-2">
                  {comment.comment}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-4 text-gray-500 text-xs">
            Nenhum comentário ainda
          </div>
        )}

        {comments && comments.length > 3 && (
          <button
            onClick={() => {
              // TODO: Abrir modal com todos os comentários ou navegar para página
              toast('Ver todos os comentários no navegador principal', {
                icon: 'ℹ️',
                style: {
                  background: '#3b82f6',
                  color: '#fff'
                }
              });
            }}
            className="w-full py-2 text-xs text-blue-400 hover:text-blue-300 
                     transition-colors underline"
          >
            Ver todos ({comments.length} comentários)
          </button>
        )}
      </div>

    </div>
  );
};
